<template>
  <div
    class="overview"
    :style="
      `
      background-image: linear-gradient(180deg, rgba(17, 17, 17, 0) 0%, #111111 100%), url(${$getPlaylistBackground(
        $route.params.id
      )});`
    "
  >
    <div class="overview__grid">
      <div class="overview__grid__header">
        <div
          v-if="certificateShortName"
          class="overview__grid__header__certificate"
        >
          <div
            class="overview__grid__header__certificate__icon-wrapper"
            :style="`background: ${learningAreaColor}`"
          >
            <CertificateIcon />
          </div>
          <span class="overview__grid__header__certificate__short-name">
            {{ certificateShortName }}
          </span>
        </div>
        <div
          v-if="learningPath.short_description"
          class="overview__grid__header__short-description"
        >
          <span>|</span>
          <span>
            {{ learningPath.short_description }}
          </span>
        </div>
      </div>
      <h2 class="overview__grid__title">
        {{ learningPath.name }}
      </h2>
      <div
        v-if="learningPath.trailer_jwplayer_id"
        class="overview__grid__video-wrapper"
      >
        <SampleVideo
          :videoId="learningPath.trailer_jwplayer_id"
          :autostart="false"
        />
      </div>
      <p
        class="overview__grid__description"
        v-html="learningPath.description"
      ></p>
      <div class="overview__grid__instructor-wrapper">
        <InstructorTile
          :learningPath="learningPath"
          :learningAreaColor="learningAreaColor"
        />
      </div>
      <div v-if="hasAccess" class="overview__grid__progress">
        <div v-if="progress > 0" class="overview__grid__progress__bar">
          <div
            class="overview__grid__progress__bar__completed"
            :style="`width: ${progress}%`"
          />
        </div>
        <div class="overview__grid__progress__actions-container">
          <button
            class="overview__resume-btn btn btn--primary"
            @click="resumePath"
          >
            <span v-if="progress > 0">Resume training</span>
            <span v-else>Begin training</span>
          </button>
          <button
            v-if="progress"
            :disabled="isRatingLoading"
            class="overview__rating-button btn btn--secondary btn--default-icon-color"
            @click="handleRateLearningPath"
          >
            <StarIcon class="overview__rating-button-icon" />
            {{ rating ? 'Review your rating' : 'Rate this learning path' }}
          </button>
        </div>
      </div>
      <div v-else class="overview__grid__cta">
        <a
          :href="unSubscribedLink"
          target="_blank"
          rel="noopener"
          class="btn btn--primary"
        >
          <LockIcon />{{ unSubscribedTCTA }}
        </a>
        <p>
          {{ unSubscribedText }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import InstructorTile from '@/components/learning-path/overview/InstructorTile.vue'
import CertificateIcon from '@/assets/icons/search/certificate.svg'
import SampleVideo from '@/components/media/SampleVideo.vue'
import { getLearningAreaSnipeColor, getLearningAreaName } from '@/helpers'
import LockIcon from '@/assets/icons/fill/lock.svg'
import { StarIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'

export default {
  name: 'Overview',
  components: {
    InstructorTile,
    CertificateIcon,
    SampleVideo,
    LockIcon,
    StarIcon,
  },
  props: {
    learningPath: {
      type: Object,
      required: true,
    },
    nextContent: {
      type: Object,
      required: false,
      default: null,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    isLabExperiencePath: {
      type: Boolean,
      required: true,
    },
    certifications: {
      type: Array,
      required: true,
    },
    unSubscribedLink: {
      type: String,
      required: true,
    },
    isRatingLoading: {
      type: Boolean,
      required: true,
    },
    rating: {
      type: Object,
      required: false,
      default: null,
    },
  },
  computed: {
    ...mapState({
      plansLink: state => state.app.plansLink,
      currentLabs: state => state.content.currentLabs,
    }),
    unSubscribedText() {
      if (this.isLabExperiencePath) {
        return `This content is part of INE's new Lab Experience. Learn more to get exclusive access to the world's best lab experience.`
      }
      return 'Get Access to this Content with an INE Subscription'
    },
    unSubscribedTCTA() {
      if (this.isLabExperiencePath) {
        return 'Learn more'
      }
      return 'Get Access'
    },
    learningAreaName() {
      return getLearningAreaName(this.learningPath)
    },
    learningAreaColor() {
      return getLearningAreaSnipeColor(this.learningAreaName)
    },
    certificateShortName() {
      return this.certifications?.[0]?.short_name
    },
    progress() {
      const progress = this.learningPath?.user_status?.progress

      if (progress) return progress === 1 ? 100 : parseInt(progress * 100)

      return 0
    },
    parentType() {
      return CONSTANTS.PARENT_CONTENT_TYPES_KEYS.LEARNING_PATH
    },
  },
  methods: {
    handleRateLearningPath() {
      this.$emit('rate-learning-path')
    },
    resumePath() {
      const { nextContent, learningAreaName } = this
      if (this.nextContent) {
        const { course, content, video } = nextContent
        const next = content || video
        switch (content.content_type) {
          case 'iframe':
            this.$store.dispatch('iframes/setPlayingIFrameId', next.id)
            break
          case 'lab':
            this.$store.dispatch('labs/setFocusLabId', next.id)
            break
          default:
            this.$store.dispatch('content/setPlayingVideoId', next.id)
            this.$store.dispatch('content/setPlayingVideoTime', 0)
            break
        }
        if (course) {
          this.$router.push(this.$buildContentLink('course', course))
        } else if (content.content_type === 'lab') {
          const currentLabs = this.currentLabs.find(
            lab => lab.id === content.id
          )
          this.$router.push(
            this.$buildContentLink(
              content.content_type,
              {
                id: content.id,
                objectID: content.id,
                slug: currentLabs.slug,
                learning_areas: [{ name: learningAreaName }],
                parent_type: this.parentType,
                parent_id: this.learningPath?.id,
              },
              this.learningPath
            )
          )
        } else {
          this.$emit('openLevel', 0, next.id)
        }
      } else {
        const {
          learningPath: { content },
        } = this
        const firstCourse = content[0].content[0]
        const { uuid: id, slug } = firstCourse
        this.$router.push(
          this.$buildContentLink('course', {
            id,
            slug,
            area: [{ name: learningAreaName }],
          })
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.overview {
  display: flex;
  justify-content: center;
  color: var(--white);
  background-size: cover;
  font-family: var(--font-book);
  padding: 100px 20px 60px;
  @include mobile {
    padding: 120px 20px;
  }
  @include desktop {
    padding: 170px 100px 120px;
  }

  &__grid {
    width: 100%;
    max-width: 1400px;

    @include mobile {
      display: grid;
      grid-auto-rows: auto;
      grid-template-columns: auto 350px;
      grid-gap: 0 10px;
    }
    @include desktop {
      grid-template-columns: auto 400px;
      grid-gap: 0;
      grid-gap: 0 50px;
    }
    &__header {
      display: flex;
      width: 100%;
      gap: 10px;
      align-items: center;
      font-size: rem(12px);
      line-height: rem(14px);
      grid-row: 1;
      grid-column: 1 / 3;

      @include mobile {
        font-size: rem(16px);
        line-height: rem(24px);
      }
      @include desktop {
        font-family: var(--font-medium);
        line-height: rem(23px);
        grid-column: 1;
      }
      &__certificate {
        display: flex;
        align-items: center;
        gap: 10px;
        &__icon-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
        }
      }

      &__short-description {
        display: flex;
        gap: 10px;
      }
    }
    &__title {
      font-family: var(--font-bold);
      font-size: rem(32px);
      line-height: rem(38px);
      margin: 10px 0 0 0;
      width: 100%;
      grid-row: 2;
      grid-column: 1 / 3;

      @include mobile {
        font-size: rem(40px);
        line-height: rem(52px);
      }
      @include desktop {
        grid-column: 1;
      }
    }
    &__video-wrapper {
      margin-top: 30px;
      grid-column: 1 / 3;

      @include desktop {
        max-width: 450px;
        grid-column: 1;
      }
    }
    &__description {
      color: var(--athens-gray);
      font-size: rem(14px);
      line-height: rem(20px);
      margin: 30px 0 0 0;

      @include mobile {
        grid-row: 4;
        grid-column: 1;
        font-size: rem(16px);
        line-height: rem(24px);
      }
      @include desktop {
        grid-row: auto;
      }

      ::v-deep a {
        color: var(--white);
        text-decoration: underline;
        &:visited {
          color: var(--white);
        }
      }
    }
    &__instructor-wrapper {
      display: flex;
      justify-content: center;
      margin-top: 30px;
      width: 100%;

      @include mobile {
        grid-column: 2;
        grid-row: 4;
      }

      @include desktop {
        grid-row: 1 / 6;
        margin: 0;
      }
    }
    &__progress {
      width: 100%;
      display: flex;
      flex-flow: row wrap;
      justify-content: flex-start;

      @include mobile {
        grid-row: 5;
        grid-column: 1 / 3;
      }
      @include desktop {
        grid-row: auto;
        grid-column: 1;
      }

      &__bar {
        width: 100%;
        height: 8px;
        background-color: var(--white-20);
        border-radius: 4px;
        margin-top: 30px;

        &__completed {
          height: inherit;
          background-color: var(--positive-light);
          border-radius: 4px;
          cursor: pointer;
        }
      }
      &__actions-container {
        width: 100%;
        margin-top: 30px;

        display: flex;
        flex-direction: column;
        gap: 10px;

        @include mobile {
          width: initial;
          flex-direction: row;
          gap: 20px;
        }
        button {
          width: 100%;
          height: 39px;

          @include mobile {
            height: 48px;
            width: initial;
          }
        }
      }
    }
    &__cta {
      display: flex;
      align-items: center;
      gap: 20px;
      width: 100%;
      margin-top: 30px;

      @include mobile {
        grid-row: 5;
        grid-column: 1 / 3;
      }
      @include desktop {
        grid-row: auto;
        grid-column: 1;
      }

      a {
        font-family: var(--font-medium);
        font-size: rem(16px);
        line-height: rem(23px);
        width: 100%;
        height: 39px;

        @include mobile {
          height: 47px;
          width: auto;
        }
      }
      p {
        display: none;

        @include mobile {
          display: initial;
          margin: 0;
          font-size: rem(14px);
          line-height: rem(20px);
          color: var(--light-blue-gray);
        }
      }
    }
  }
}
</style>
